import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { PageSectionContent } from '@types';
import { SectionComponent } from '../section-component.interface';
import { Logger } from '@utils/logger';
import { Card } from '@frk/eds-components';

/**
 * Logger
 */
const logger = Logger.getLogger('ThumbnailsSectionComponent');

@Component({
  selector: 'ft-thumbnails-section',
  templateUrl: './thumbnails-section.component.html',
  styleUrls: ['./thumbnails-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThumbnailsSectionComponent implements OnInit, SectionComponent {
  /**
   * Bloomreach component
   */
  component: BrComponent;

  /**
   * Bloomreach page model
   */
  page: Page;

  /**
   * Page Section content
   */
  content: PageSectionContent;

  /**
   * Display type
   */
  sectionType: string;

  /**
   * Cards Array
   */
  cards: Array<any>;

  /**
   * Theme
   */
  theme: string;

  PDF_ICON_PATH = '/assets/icons/file-icons/ft-global-icon-pdf.svg';
  PREVIEW_ICON_PATH = '/assets/icons/file-icons/pdf-preview.png';

  constructor() {}

  /**
   * Populate the component
   * @param component - A parameter
   * @param page - A parameter
   * @param content - A parameter
   */
  populate(
    component: BrComponent,
    page: Page,
    sectionType: string,
    content: PageSectionContent,
    theme: string
  ) {
    this.component = component;
    this.page = page;
    this.sectionType = sectionType;
    this.content = content;
    if (this.content?.cards) {
      this.cards = this.formatCards(this.content?.cards);
      this.cards = this.createCardGroups(this.content.cards, 2);
      this.theme = theme || 'primary';
    }
  }

  /**
   * Users are entering content inconsistently for PDF links
   * Here we apply a simple set of rules to try and determine
   * what text to show.
   * @param card - card object
   */
  formatCards(cards): Card[] {
    const formatted: Array<Card> = [];
    for (const card of cards) {
      // title
      card.title = this.getTitle(card);
      // default image
      card.imgSrc = this.getPreviewIcon(card);
      // button text
      if (card.link) {
        card.link.displayText = this.getDisplayText(card);
      }
      // file icon
      card.docTypeSrc = this.getFileTypeIcon(card);
    }
    return formatted;
  }

  /**
   * A simplistic check to fix content which has incorrectly been linked to pdf svg.
   * @param imgPath - path to widen image
   */
  isPdfIcon(imgPath: string): boolean {
    return (
      imgPath?.includes('ft-global-icon-pdf') || imgPath?.includes('PDF_file')
    );
  }

  /**
   * Users are entering content inconsistently for PDF links
   * Here we apply a simple set of rules to try and determine
   * what text to show.
   * @param card - card object
   */
  getDisplayText(card): string {
    let label;
    // If button has display text
    if (card.link?.displayText) {
      label = card.link?.displayText;
    } else if (card.link?.title) {
      label = card.link?.title;
    }
    // If label is still blank
    if (!label) {
      label = card.title;
    }
    return label;
  }

  /**
   * Users are entering content inconsistently for PDF links
   * Here we apply a simple set of rules to try and determine
   * what text to show.
   * @param card - card object
   */
  getPreviewIcon(card) {
    // Ignore pdf icons and set preview image instead
    let src = this.isPdfIcon(card.imageSrc) ? null : card.imageSrc;
    if (!src && card.content) {
      src = this.PREVIEW_ICON_PATH;
    }
    logger.debug(src);
    return src;
  }

  /**
   * Users are entering content inconsistently for PDF links
   * Here we apply a simple set of rules to try and determine
   * what text to show.
   * @param card - card object
   */
  getTitle(card): string {
    let label;
    // If card has title
    if (card.title) {
      label = card.title;
    } else if (card.link?.title) {
      label = card.link?.title;
    }

    // If label is still blank
    if (!label) {
      label = card.link?.displayText;
    }
    return label;
  }

  /**
   * Apply some logic to determine if file is PDF
   * TODO: Add a file type option to the content
   */
  getFileTypeIcon(card): string {
    // Simplistic check
    logger.debug('getFileTypeIcon; ' + card.link?.url);
    if (
      card.link?.url?.endsWith('pdf') ||
      card.link?.url?.includes('/literature/info/') ||
      card.link?.url?.includes('/downloadLit/') ||
      card.link?.url?.includes('widen')
    ) {
      return this.PDF_ICON_PATH;
    }
  }

  /**
   * Split cards into groups
   * @param cards - cards array
   */
  createCardGroups(array, size) {
    const cardGroups = [];
    let index = 0;
    while (index < array.length) {
      cardGroups.push(array.slice(index, size + index));
      index += size;
    }
    return cardGroups;
  }

  ngOnInit(): void {}
}
